import { ButtonAction } from '@heidi/analytics/vocabulary';
import { TierProFilled } from '@heidi/design-system/icons';
import { DialogDrawerBody } from '@heidi/design-system/mobile-responsive-dialog-drawer';
import { useTranslations } from '@heidi/runtime/i18n';
import { Blocks, Pencil, PenTool, Rocket, Snail, Sparkles, Star } from 'lucide-react';
import { type Period, type ProductView } from '../../../../../contracts';
import { getCommerceBillingUiPorts } from '../../../../settings/billing/billing-ui-ports';
import { ProductCard } from '../product-card';

interface ModalContentProps {
  period: Period;
  product: ProductView | undefined;
  onStayPro: () => void;
  onGoFree: () => void;
  loadingStayPro: boolean;
  loadingGoFree: boolean;
}

export function EndPromotedModalContent({
  period,
  product,
  onStayPro,
  onGoFree,
  loadingStayPro: loadingStayPro,
  loadingGoFree: loadingGoFree,
}: ModalContentProps) {
  const { t } = useTranslations();
  const commerceBillingUiPorts = getCommerceBillingUiPorts();
  const TrackedButton = commerceBillingUiPorts.TrackedButton;
  const isLoading = loadingStayPro || loadingGoFree;

  return (
    <DialogDrawerBody className="grid w-full grid-cols-2 gap-4">
      <ProductCard
        variant="pro"
        title="endTrialModal.proPlan.title"
        details={[
          {
            icon: PenTool,
            iconProps: {
              className: '-rotate-90',
            },
            translationKey: 'endTrialModal.proPlan.features.unlimitedProScribe',
          },
          {
            icon: Blocks,
            translationKey: 'endTrialModal.proPlan.features.unlimitedProTemplates',
          },
          {
            icon: Sparkles,
            translationKey: 'endTrialModal.proPlan.features.unlimitedPrompts',
          },
          {
            icon: Rocket,
            translationKey: 'endTrialModal.proPlan.features.prioritySpeed',
          },
        ]}
        product={product}
        ctaButton={
          !commerceBillingUiPorts.isAppleCompliantBuild() ? (
            <TrackedButton
              size="lg"
              actionTrackingName={ButtonAction.EndPromotedFreeTrialKeepPro}
              variant="pro"
              className="w-full"
              disabled={isLoading}
              onClick={() => void onStayPro()}
            >
              <TierProFilled size={16} />
              {!loadingStayPro
                ? t('endTrialModal.proPlan.button.default')
                : t('endTrialModal.proPlan.button.loading')}
            </TrackedButton>
          ) : undefined
        }
        period={period}
      />
      <ProductCard
        variant="free"
        title="endTrialModal.freePlan.title"
        product={product}
        details={[
          {
            icon: Pencil,
            translationKey: 'endTrialModal.freePlan.features.limitedFreeScribe',
          },
          {
            icon: Blocks,
            translationKey: 'endTrialModal.freePlan.features.limitedTemplates',
          },
          {
            icon: Star,
            translationKey: 'endTrialModal.freePlan.features.limitedPrompts',
          },
          {
            icon: Snail,
            translationKey: 'endTrialModal.freePlan.features.basicSpeed',
          },
        ]}
        ctaButton={
          <TrackedButton
            size="lg"
            actionTrackingName={ButtonAction.EndPromotedFreeTrialDowngradeToFree}
            variant="secondary"
            className="w-full"
            onClick={onGoFree}
            disabled={isLoading}
          >
            {!loadingGoFree
              ? t('endTrialModal.freePlan.button.default')
              : t('endTrialModal.freePlan.button.loading')}
          </TrackedButton>
        }
        period={period}
      />
    </DialogDrawerBody>
  );
}
